import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, Truck, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

export default function CartSheet() {
  const { items, updateQty, removeItem, subtotal, cloneQty, open, setOpen, hasPreorder, hasReady } = useCart();
  const navigate = useNavigate();
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    api.get("/promos/featured").then(({ data }) => setPromo(data && data.code ? data : null)).catch(() => {});
  }, []);

  const eligible = promo && cloneQty >= promo.min_clones;
  const needed = promo ? promo.min_clones - cloneQty : 0;
  const showNudge = promo && cloneQty >= 1 && cloneQty < promo.min_clones;

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-sheet"
        className="bg-[#050505] border-l border-white/10 text-slate-200 w-full sm:max-w-md flex flex-col p-0"
      >
        <SheetHeader className="px-6 py-5 border-b border-white/10">
          <SheetTitle className="font-display text-3xl text-slate-100 text-left">Your Cart</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-4">
            <ShoppingBag size={40} strokeWidth={1} className="text-slate-600" />
            <p className="text-slate-500 text-sm">Your cart is empty.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((item) => (
                <div key={item.product_id} data-testid={`cart-item-${item.product_id}`} className="flex gap-4">
                  <img src={item.image_url} alt={item.name} loading="lazy" decoding="async" className="w-20 h-20 object-cover bg-black border border-white/5" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <p className="font-display text-lg text-slate-100 leading-tight">{item.name}</p>
                      <button
                        data-testid={`cart-remove-${item.product_id}`}
                        onClick={() => removeItem(item.product_id)}
                        className="text-slate-600 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <p className="text-emerald-400 text-sm mt-1">${item.price.toFixed(2)}</p>
                    {item.availability === "preorder" && (
                      <p data-testid={`cart-preorder-${item.product_id}`} className="text-[0.7rem] text-amber-400 mt-1">
                        Made to order — roots in ~2 weeks, then ships next day
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        onClick={() => updateQty(item.product_id, item.quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center border border-white/15 hover:border-emerald-500 transition-colors"
                        data-testid={`cart-dec-${item.product_id}`}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-sm w-5 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQty(item.product_id, item.quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center border border-white/15 hover:border-emerald-500 transition-colors"
                        data-testid={`cart-inc-${item.product_id}`}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 px-6 py-6 space-y-4">
              {showNudge && (
                <div data-testid="cart-promo-nudge" className="flex items-center gap-2 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-3 py-2.5">
                  <Tag size={13} className="shrink-0" />
                  <span>Add {needed} more clone{needed > 1 ? "s" : ""} to unlock code <span className="font-semibold">{promo.code}</span> — {promo.discount_percent}% off.</span>
                </div>
              )}
              {eligible && (
                <div data-testid="cart-promo-eligible" className="flex items-center gap-2 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-3 py-2.5">
                  <Tag size={13} className="shrink-0" />
                  <span>Use code <span className="font-semibold">{promo.code}</span> at checkout for {promo.discount_percent}% off your order.</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Subtotal</span>
                <span data-testid="cart-subtotal" className="text-slate-100">${subtotal.toFixed(2)}</span>
              </div>
              <p className="flex items-center gap-1.5 text-xs text-slate-500">
                <Truck size={13} className="text-emerald-500" /> Free next-day shipping · Discreet packaging.
              </p>
              {hasPreorder && hasReady && (
                <p data-testid="cart-mixed-note" className="text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 px-3 py-2.5">
                  Your order ships together once all cuts are rooted.
                </p>
              )}
              <button
                data-testid="checkout-button"
                onClick={goCheckout}
                className="w-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors font-medium py-3.5 text-sm tracking-wide"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
